import type {PolymorphicComponentWithDefault} from '@github-ui/react-polymorphic'
import {TextInput, type TextInputProps} from '@primer/react'
import {useCallback, useEffect, useImperativeHandle, useRef} from 'react'

import styles from './autosize-text-input.module.css'

/**
 * Copies styles to a list of elements.
 *
 * @param styles
 * @param nodes
 */
const copyStylesToNodes = (cssStyles: HTMLElement['style'], nodes: ReadonlyArray<HTMLElement | null>) => {
  for (const node of nodes) {
    if (node) {
      node.style.font = cssStyles.font
      node.style.fontSize = cssStyles.fontSize
      node.style.fontFamily = cssStyles.fontFamily
      node.style.fontWeight = cssStyles.fontWeight
      node.style.fontStyle = cssStyles.fontStyle
      node.style.letterSpacing = cssStyles.letterSpacing
      node.style.textTransform = cssStyles.textTransform
    }
  }
}

function getSizerValue(...values: ReadonlyArray<string | number | ReadonlyArray<string> | undefined>) {
  for (const value of values) {
    if (value !== null && value !== undefined) {
      return value.toString()
    }
  }
  return ''
}

interface AutosizeTextInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement> & Omit<TextInputProps, 'sx'>,
  'style'
> {
  /** a minWidth for the input */
  minWidth?: number
  containerClassName?: string
}

/**
 * An input field that automatically sizes to fit its value contents.
 */
export const AutosizeTextInput: PolymorphicComponentWithDefault<typeof TextInput, AutosizeTextInputProps> = ({
  ref: forwardedRef,
  minWidth = 1,
  as: As = TextInput,
  containerClassName,
  onChange,
  onBlur,
  ...props
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const sizerRef = useRef<HTMLDivElement>(null)
  const placeholderRef = useRef<HTMLDivElement>(null)

  /** delegate the internal ref to the forwarded one if there is one */
  useImperativeHandle<HTMLInputElement | null, HTMLInputElement | null>(forwardedRef, () => inputRef.current, [])

  /** Dynamically compute the width for the input */
  const resizeInput = useCallback(() => {
    // Querying the input-styles in a rAF ensures that the font is correctly reported.
    // See - https://github.com/github/memex/issues/4332
    requestAnimationFrame(() => {
      const {current: currentSizer} = sizerRef
      const {current: placeholderSizer} = placeholderRef
      if (!currentSizer) {
        return
      }

      const inputStyles = inputRef.current && window.getComputedStyle(inputRef.current)
      if (inputStyles) {
        copyStylesToNodes(inputStyles, [currentSizer, placeholderSizer])
      }

      const currentSizerScrollWidth = currentSizer.scrollWidth
      const placeholderSizerScrollWidth = placeholderSizer?.scrollWidth ?? 0

      const newInputWidth = Math.max(
        currentSizerScrollWidth + 2,
        inputRef.current?.value === '' ? placeholderSizerScrollWidth + 2 : 0,
        minWidth,
      )
      if (inputRef.current) {
        inputRef.current.style.width = `${newInputWidth}px`
      }
    })
  }, [minWidth])

  useEffect(() => {
    resizeInput()
  })

  return (
    <div className={containerClassName}>
      <div ref={sizerRef} aria-hidden="true" className={styles.autosizeTextInputContainer}>
        {getSizerValue(props.defaultValue, props.value)}
      </div>
      {props.placeholder ? (
        <div ref={placeholderRef} aria-hidden="true" className={styles.autosizeTextInputContainer}>
          {props.placeholder}
        </div>
      ) : null}
      <As
        {...props}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          resizeInput()
          onChange?.(e)
        }}
        onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
          resizeInput()
          onBlur?.(e)
        }}
        ref={inputRef}
      />
    </div>
  )
}
